import { Coins, Network } from "lucide-react";

import { useWordstatExpenseQuery } from "@/entities/projects/queries";
import type { WordstatExpenseOut } from "@/entities/projects/types";
import { cn } from "@/shared/lib/cn";
import { formatNumber } from "@/shared/lib/format";

/**
 * Виджет расхода Wordstat — две карточки «за час» и «сегодня».
 *
 * Из шапки убрана фракция «X/100 запросов» и время восстановления:
 * на платном тарифе Yandex AI Studio часовой квоты как таковой нет
 * (есть soft rate-limit, но не жёсткий потолок).
 */
export function WordstatExpenseWidget(): JSX.Element {
  const hourQ = useWordstatExpenseQuery("hour");
  const todayQ = useWordstatExpenseQuery("today");

  return (
    <div className="grid gap-4 sm:grid-cols-2">
      <PeriodCard
        title="Wordstat · этот час"
        accent="cyan"
        icon={Network}
        data={hourQ.data}
        loading={hourQ.isLoading}
      />
      <PeriodCard
        title="Wordstat · сегодня"
        accent="purple"
        icon={Coins}
        data={todayQ.data}
        loading={todayQ.isLoading}
      />
    </div>
  );
}

function PeriodCard({
  title,
  accent,
  icon: Icon,
  data,
  loading,
}: {
  title: string;
  accent: "cyan" | "purple";
  icon: React.ComponentType<{ className?: string }>;
  data: WordstatExpenseOut | undefined;
  loading: boolean;
}): JSX.Element {
  const top = data?.by_method.topRequests?.count ?? 0;
  const dyn = data?.by_method.dynamicRequests?.count ?? 0;

  return (
    <div className="glass-card p-5">
      <div className="flex items-center gap-2">
        <div
          className={cn(
            "flex h-9 w-9 items-center justify-center rounded-xl text-white",
            accent === "cyan"
              ? "bg-gradient-to-br from-cyan to-cyan-dark shadow-glow-cyan"
              : "bg-gradient-to-br from-purple to-purple-dark shadow-glow-purple",
          )}
        >
          <Icon className="h-4 w-4" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
            {title}
          </div>
          <div className="font-mono text-xl font-bold text-white tabular-nums">
            {loading ? "—" : formatNumber(data?.requests_count ?? 0)}{" "}
            <span className="text-base font-medium text-cyan">
              · {data ? data.cost_rub.toFixed(2) : "0.00"} ₽
            </span>
          </div>
        </div>
      </div>

      <div className="mt-3 grid grid-cols-2 gap-2 text-xs">
        <MethodRow label="topRequests" count={top} />
        <MethodRow label="dynamicRequests" count={dyn} />
      </div>
    </div>
  );
}

function MethodRow({
  label,
  count,
}: {
  label: string;
  count: number;
}): JSX.Element {
  return (
    <div className="rounded-lg border border-white/10 bg-white/[0.03] px-3 py-2">
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground">
        {label}
      </div>
      <div className="font-mono text-sm font-bold tabular-nums text-white">
        {formatNumber(count)}
      </div>
    </div>
  );
}
